import CryptoJS from 'crypto-js';
import { getCsrfToken } from './csrfTokenCache';
import { devLog, devWarn } from '../utils/devLog';

// API Service with JWT authentication
class ApiService {
  constructor() {
    this.baseURL = process.env.NODE_ENV === 'production'
      ? (process.env.REACT_APP_API_URL || 'https://blp-be.onrender.com')
      : 'http://localhost:5000';
  }

  // Delegate to the shared CSRF cache so apiService and authService stay
  // in sync about which token matches the current cookie.
  async getCsrfToken(opts = {}) {
    return getCsrfToken(opts);
  }

  // Get JWT token from cookies (handled by browser automatically)
  getAuthToken() {
    // For cookie-based auth, we don't need to manually get the token
    // The browser will automatically include it in requests
    return null;
  }

  // Set JWT token (now handled via httpOnly cookies)
  setAuthToken(token) {
    // For httpOnly cookies, we can't set them from JavaScript
    // They are set by the server in the login response
    devLog('Token management now handled via httpOnly cookies');
  }

  // Check if user is authenticated by making a validation request
  async isAuthenticated() {
    try {
      const response = await this.apiCall('/api/auth/validate', {
        method: 'GET'
      });
      
      if (response.ok) {
        const data = await response.json();
        return data.valid || false;
      }
      
      return false;
    } catch (error) {
      console.error('Authentication check failed:', error);
      return false;
    }
  }

  // Hash password for secure transmission
  // Using a deterministic approach based on email for consistency
  hashPassword(password, email) {
    // Create a deterministic salt based on email (not exposed to server)
    const salt = CryptoJS.SHA256(email + 'bluelagoon_secure_salt_2024').toString();
    
    // Hash password with salt using PBKDF2
    const hashedPassword = CryptoJS.PBKDF2(password, salt, {
      keySize: 256/32,
      iterations: 10000
    }).toString();
    
    return hashedPassword;
  }

  // Handle rate limiting with exponential backoff
  async handleRateLimit(response) {
    if (response.status === 429) {
      const retryAfter = response.headers.get('Retry-After') || 1;
      devLog(`Rate limited. Retrying after ${retryAfter} seconds...`);
      
      // Wait for the specified time plus a small random delay
      await new Promise(resolve => 
        setTimeout(resolve, (parseInt(retryAfter) * 1000) + Math.random() * 500)
      );
      
      return true; // Indicates retry is needed
    }
    return false;
  }

  // Main API call method with cookie-based authentication
  async apiCall(endpoint, options = {}, maxRetries = 3) {
    const url = `${this.baseURL}${endpoint}`;
    
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      const requestOptions = {
        ...options,
        credentials: 'include', // Include cookies in requests
        headers: {
          ...options.headers
        }
      };

      // Only set Content-Type to application/json if not already specified and body is not FormData
      if (!options.headers?.hasOwnProperty('Content-Type') && !(options.body instanceof FormData)) {
        requestOptions.headers['Content-Type'] = 'application/json';
      }

      // Attach CSRF token to state-changing requests
      const reqMethod = (options.method || 'GET').toUpperCase();
      if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(reqMethod)) {
        try {
          const token = await this.getCsrfToken();
          if (token) requestOptions.headers['X-CSRF-Token'] = token;
        } catch (e) {
          devWarn('CSRF token fetch failed:', e.message);
        }
      }

      try {
        devLog(`API Call: ${options.method || 'GET'} ${endpoint} (Cookie-based auth)`);
        
        const response = await fetch(url, requestOptions);
        
        // Handle rate limiting
        if (await this.handleRateLimit(response)) {
          if (attempt < maxRetries) {
            continue; // Retry the request
          }
        }
        
        // Handle token expiration
        if (response.status === 401) {
          devLog('Authentication expired or required');
          // For cookie-based auth, the server will clear the cookie
          // Don't retry for auth endpoints to avoid infinite loops
          if (!endpoint.includes('/auth/')) {
            devLog('Authentication required for this request');
          }
        }

        // Handle CSRF token expiration — refresh once and retry the request
        if (response.status === 403 && attempt === 0) {
          try {
            const errBody = await response.clone().json();
            if (errBody?.error?.code === 'CSRF_TOKEN_INVALID') {
              devLog('Token invalid — refreshing and retrying');
              await this.getCsrfToken({ forceRefresh: true });
              continue;
            }
          } catch {
            // Not JSON or no error code — fall through to return the 403
          }
        }
        
        // Log response details
        devLog(`Response: ${response.status} ${response.statusText}`);
        devLog(`Headers:`, Object.fromEntries(response.headers.entries()));
        
        return response;
        
      } catch (error) {
        console.error(`API call failed (attempt ${attempt + 1}):`, error);
        
        if (attempt === maxRetries) {
          throw new Error(`API call failed after ${maxRetries + 1} attempts: ${error.message}`);
        }
        
        // Wait before retry with exponential backoff
        await new Promise(resolve => 
          setTimeout(resolve, Math.pow(2, attempt) * 1000 + Math.random() * 1000)
        );
      }
    }
  }

  // Authentication methods
  async login(email, password) {
    // Hash the password before sending (salt and algorithm kept secure on client-side)
    const passwordHash = this.hashPassword(password, email);
    
    const response = await this.apiCall('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ 
        email, 
        passwordHash
      })
    });
    
    return response.json();
  }

  async register(userData) {
    const response = await this.apiCall('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify(userData)
    });
    
    return response.json();
  }

  async forgotPassword(email) {
    const response = await this.apiCall('/api/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email })
    });
    
    return response.json();
  }

  async resetPassword(token, newPassword) {
    const response = await this.apiCall('/api/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, newPassword })
    });
    
    return response.json();
  }

  // Guides methods
  async getGuides(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = `/api/guides${queryString ? `?${queryString}` : ''}`;
    
    const response = await this.apiCall(endpoint);
    return response.json();
  }

  async getGuide(id) {
    const response = await this.apiCall(`/api/guides/${id}`);
    return response.json();
  }

  async searchGuides(query, filters = {}) {
    const params = { q: query, ...filters };
    const queryString = new URLSearchParams(params).toString();
    
    const response = await this.apiCall(`/api/guides/search/query?${queryString}`);
    return response.json();
  }

  async accessGuide(id) {
    const response = await this.apiCall(`/api/guides/${id}/access`, {
      method: 'POST'
    });
    
    return response.json();
  }

  async unlockGuide(id, method = 'login') {
    const response = await this.apiCall(`/api/guides/${id}/unlock`, {
      method: 'POST',
      body: JSON.stringify({ method })
    });
    
    return response.json();
  }

  async getGuideContent(id) {
    const response = await this.apiCall(`/api/guides/${id}/content`);
    return response.json();
  }

  async validateSession() {
    // For JWT authentication, we can validate locally
    const token = this.getAuthToken();
    if (!token) {
      return { valid: false, error: 'No token found' };
    }
    
    try {
      // Basic JWT structure check
      const parts = token.split('.');
      if (parts.length !== 3) {
        return { valid: false, error: 'Invalid token format' };
      }
      
      // Decode payload to check expiration
      const payload = JSON.parse(atob(parts[1]));
      const now = Math.floor(Date.now() / 1000);
      
      if (payload.exp <= now) {
        return { valid: false, error: 'Token expired' };
      }
      
      return { 
        valid: true, 
        user: payload,
        expiresAt: payload.exp 
      };
    } catch (error) {
      return { valid: false, error: 'Token validation failed' };
    }
  }

  // Marketplace methods with authentication support
  async getMarketplace(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = `/api/marketplace${queryString ? `?${queryString}` : ''}`;
    
    try {
      const response = await this.apiCall(endpoint);
      const data = await response.json();
      
      // Process the response to ensure biddingEndTime is properly formatted
      if (data.success && data.data && data.data.properties) {
        data.data.properties = data.data.properties.map(property => {
          // For guest users, show all active properties (authentication check removed to prevent rate limiting)
          // The server will handle filtering based on authentication status
          
          // Ensure the property has all required fields
          return {
            ...property,
            // If timeLeft exists but biddingEndTime doesn't, calculate an approximate end time
            biddingEndTime: property.biddingEndTime || this.calculateEndTimeFromTimeLeft(property.timeLeft)
          };
        }).filter(Boolean); // Remove null entries
      }
      
      return data;
    } catch (error) {
      console.error('Error fetching marketplace data:', error);
      return { 
        success: false, 
        error: { message: 'Failed to fetch marketplace data' } 
      };
    }
  }
  
  // Helper to calculate an approximate end time from timeLeft string
  calculateEndTimeFromTimeLeft(timeLeft) {
    if (!timeLeft) return null;
    
    const now = new Date();
    const parts = timeLeft.split(' ');
    let totalMinutes = 0;
    
    for (let i = 0; i < parts.length; i += 2) {
      const value = parseInt(parts[i]);
      const unit = parts[i + 1];
      
      if (unit && unit.startsWith('d')) totalMinutes += value * 24 * 60;
      else if (unit && unit.startsWith('h')) totalMinutes += value * 60;
      else if (unit && unit.startsWith('m')) totalMinutes += value;
    }
    
    const endTime = new Date(now.getTime() + totalMinutes * 60 * 1000);
    return endTime.toISOString();
  }

  async getProperty(id) {
    const response = await this.apiCall(`/api/marketplace/${id}`);
    return response.json();
  }

  async getPropertyDetails(id) {
    const response = await this.apiCall(`/api/properties/${id}`);
    return response.json();
  }

  async placeBid(propertyId, bidAmount) {
    const response = await this.apiCall(`/api/marketplace/${propertyId}/bid`, {
      method: 'POST',
      body: JSON.stringify({ bidAmount })
    });
    
    return response.json();
  }

  async getPropertyPricing(id) {
    const response = await this.apiCall(`/api/marketplace/${id}/pricing`);
    return response.json();
  }

  async getPropertyBids(id) {
    const response = await this.apiCall(`/api/marketplace/${id}/bids`);
    return response.json();
  }

  async getPropertyDocuments(id) {
    const response = await this.apiCall(`/api/properties/${id}/documents`);
    return response.json();
  }

  async getPropertyAnalysis(id) {
    const response = await this.apiCall(`/api/properties/${id}/analysis`);
    return response.json();
  }

  async expressInterest(propertyId, interestData) {
    const response = await this.apiCall(`/api/properties/${propertyId}/interest`, {
      method: 'POST',
      body: JSON.stringify(interestData)
    });
    
    return response.json();
  }

  // Search methods
  async searchProperties(query, filters = {}) {
    const params = { q: query, ...filters };
    const queryString = new URLSearchParams(params).toString();
    
    const response = await this.apiCall(`/api/properties/search/query?${queryString}`);
    return response.json();
  }

  // Utility methods
  async getFilterOptions() {
    const response = await this.apiCall('/api/marketplace/filters/options');
    return response.json();
  }

  async getGuideCategories() {
    const response = await this.apiCall('/api/guides/meta/categories');
    return response.json();
  }

  // Contact methods
  async submitContact(contactData) {
    const response = await this.apiCall('/api/contact', {
      method: 'POST',
      body: JSON.stringify(contactData)
    });
    
    return response.json();
  }

  async subscribeNewsletter(email, preferences = []) {
    const response = await this.apiCall('/api/contact/newsletter', {
      method: 'POST',
      body: JSON.stringify({ email, preferences })
    });
    
    return response.json();
  }

  async requestCallback(callbackData) {
    const response = await this.apiCall('/api/contact/callback', {
      method: 'POST',
      body: JSON.stringify(callbackData)
    });
    
    return response.json();
  }

  async getContactInfo() {
    const response = await this.apiCall('/api/contact/info');
    return response.json();
  }

  // Careers methods
  async submitCareerApplication(applicationData) {
    const response = await this.apiCall('/api/careers/apply', {
      method: 'POST',
      body: applicationData, // FormData object
      headers: {} // Let browser set Content-Type for FormData
    });
    
    return response.json();
  }

  // Waitlist methods
  async submitWaitlist(email) {
    const response = await this.apiCall('/api/waitlist', {
      method: 'POST',
      body: JSON.stringify({ email })
    });
    
    return response.json();
  }

  // Health check
  async healthCheck() {
    try {
      const response = await this.apiCall('/api/health');
      return response.json();
    } catch (error) {
      return { status: 'error', message: error.message };
    }
  }
}

// Create and export singleton instance
const apiService = new ApiService();
export default apiService;

// Export individual methods for convenience
export const {
  login,
  register,
  forgotPassword,
  resetPassword,
  getGuides,
  getGuide,
  searchGuides,
  accessGuide,
  getMarketplace,
  getProperty,
  getPropertyDetails,
  placeBid,
  getPropertyPricing,
  getPropertyBids,
  getPropertyDocuments,
  getPropertyAnalysis,
  expressInterest,
  searchProperties,
  getFilterOptions,
  getGuideCategories,
  submitWaitlist,
  healthCheck
} = apiService;
